"use client";

import { useEffect, useRef, useState } from "react";
import type { EventLogState } from "@/lib/sse/useSSE";
import { usePresenterHotkeys } from "@/lib/presenter/usePresenterHotkeys";
import { useSignDoc } from "@/lib/sign/SignDocContext";
import { useUserActions } from "@/lib/userActions/UserActionsContext";
import { inferActiveScenario } from "@/lib/presenter/inferActiveScenario";
import type { ScenarioCatalogue, ScenarioBoundary, ScenarioScreenCheckpoint } from "@/lib/presenter/types";
import { ScenarioPlayMenu } from "@/components/scene/chrome/ScenarioPlayMenu";
import { ManualModeToggle } from "@/components/scene/chrome/ManualModeToggle";
import { WarpControls }     from "@/components/scene/chrome/WarpControls";
import { RewindControls }   from "@/components/scene/chrome/RewindControls";
import { TimelineScrubber } from "@/components/scene/chrome/TimelineScrubber";
import { InjectMenu }       from "@/components/scene/chrome/InjectMenu";
import { HotkeysLegend }    from "@/components/scene/chrome/HotkeysLegend";
import { KeyboardHelpOverlay } from "@/components/scene/KeyboardHelpOverlay";

/**
 * Presenter chrome — hidden by default, toggled with `?` / `Cmd+,`.
 *
 *   Layout: bottom-right floating panel, viewport-bounded so it never
 *   overflows on a projector (max-w + max-h + overflow-y-auto).
 *
 *   The chrome itself owns state (chromeOpen, warp, manualMode,
 *   paused, catalogue), the catalogue fetch, the post helpers, and
 *   the hotkey bindings. The visible UI is composed from one
 *   presentational subcomponent per section, all in `./chrome/`.
 *
 *   Hotkey library (per Decision 12):
 *     E / I            perspective override
 *     N                advance one screen (manual mode)
 *     Shift+N          rewind to previous screen checkpoint
 *     Shift+B          rewind to most recent act boundary
 *     L                cycle language (EN → AR → PT → EN)
 *     ?  / Cmd+,       toggle chrome reveal
 *     Esc              dismiss any open chrome / drawer
 */

const MANUAL_MODE_KEY = "orbitra-manual-mode";

export function PresenterChrome({
  ev,
  baseUrl = "/api",
  onPerspectiveOverride,
}: {
  ev: EventLogState;
  baseUrl?: string;
  onPerspectiveOverride?: (override: string | null) => void;
}) {
  const [chromeOpen, setChromeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [warp, setWarp] = useState(1);
  // Manual mode defaults ON — the click-driven CTAs across the trade
  // arcs (Continue, Submit, Create proposal, etc.) all POST
  // /control/next, which only releases the next event batch when the
  // emitter is in step mode. Auto-play stays available via the chrome
  // toggle; presenters who want hands-free narration can flip it.
  const [manualMode, setManualMode] = useState(true);
  // Local pause state — toggles the pause/resume button. Any action
  // that starts/restarts playback (scenario play, reset, seek) clears
  // it so the toggle stays in sync with backend playback state.
  const [paused, setPaused] = useState(false);
  const [catalogue, setCatalogue] = useState<ScenarioCatalogue>({});

  // Restore manual-mode preference from localStorage (per-laptop scope).
  // If nothing was stored, the `true` default above stands. We only
  // override to `false` when the localStorage entry is the literal
  // string "false" — preserves prior presenter preference if they
  // explicitly opted into auto-play on this laptop.
  useEffect(() => {
    try {
      if (localStorage.getItem(MANUAL_MODE_KEY) === "false") setManualMode(false);
    } catch { /* localStorage unavailable */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem(MANUAL_MODE_KEY, manualMode ? "true" : "false"); } catch { /* noop */ }
  }, [manualMode]);

  // Load scenario catalogue once. Check r.ok BEFORE r.json() — a
  // non-OK response (auth redirect, 5xx) would otherwise crash JSON
  // parsing and the catch would swallow the root cause. With this
  // guard the failure surfaces in the console so catalogue-empty bugs
  // (cookie / cors / proxy issues) are diagnosable.
  useEffect(() => {
    fetch(`${baseUrl}/scenarios`)
      .then((r) => {
        if (!r.ok) {
          // eslint-disable-next-line no-console
          console.warn(`[chrome] /scenarios returned ${r.status} ${r.statusText}`);
          return null;
        }
        return r.json();
      })
      .then((data) => { if (data) setCatalogue(data); })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[chrome] /scenarios fetch failed:", err);
      });
  }, [baseUrl]);

  // Focus-restoration when chrome closes — keeps keyboard focus from
  // getting stranded on body after the audience hits Esc.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (chromeOpen) {
      restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current.focus();
      restoreFocusRef.current = null;
    }
  }, [chromeOpen]);

  const post = async (path: string) => {
    try { await fetch(`${baseUrl}${path}`, { method: "POST" }); } catch { /* noop */ }
  };

  // UserActions persist in sessionStorage so a CTA-gate clicked on
  // (say) T1 2.1 stays "fired" across Shift+N rewinds within the same
  // scenario run. But a fresh /control/play or /control/reset is the
  // start of a NEW run — the gates should re-arm. Clear UserActions
  // at those two boundaries only; per-screen rewinds keep state.
  const userActions = useUserActions();
  const resetUserActions = () => userActions.clear();

  const seekToActBoundary = () => {
    if (!ev.currentScreen) return;
    const scenarioId = inferActiveScenario(ev);
    const acts = (catalogue[scenarioId]?.actBoundaries ?? []) as ScenarioBoundary[];
    if (!acts.length) return;
    const current = ev.currentAct?.act;
    const target = acts.find((a) => a.act === current) ?? acts[0];
    void post(`/control/seek?offsetMs=${target.offsetMs}`);
    setPaused(false);
  };

  const seekToPreviousScreen = () => {
    const scenarioId = inferActiveScenario(ev);
    const screens = (catalogue[scenarioId]?.screenCheckpoints ?? []) as ScenarioScreenCheckpoint[];
    if (!screens.length || !ev.currentScreen) return;
    const idx = screens.findIndex((s) => s.screen === ev.currentScreen!.screen);
    const prev = idx > 0 ? screens[idx - 1] : screens[0];
    void post(`/control/seek?offsetMs=${prev.offsetMs}`);
    setPaused(false);
  };

  const { reopenActive: reopenSignDoc } = useSignDoc();

  usePresenterHotkeys({
    onPerspectiveExporter: () => onPerspectiveOverride?.(inferActiveScenario(ev) === "t3-reverse-corridor" ? "E-GP" : "E"),
    onPerspectiveImporter: () => onPerspectiveOverride?.(inferActiveScenario(ev) === "t3-reverse-corridor" ? "I-SV" : "I-AM"),
    onCycleLocale: () => {
      const params = new URLSearchParams(window.location.search);
      const cur = params.get("lang") ?? "en";
      const next = cur === "en" ? "ar" : cur === "ar" ? "pt" : "en";
      params.set("lang", next);
      window.location.search = params.toString();
    },
    onRewindAct:    seekToActBoundary,
    onRewindScreen: seekToPreviousScreen,
    onAdvanceScreen: () => post("/control/next"),
    onToggleChrome: () => setChromeOpen((v) => !v),
    onToggleHelp:   () => setHelpOpen((v) => !v),
    onEscape: () => {
      // Esc cascades: dismiss help overlay first, then chrome. Lets
      // presenter close them in order without two key presses.
      if (helpOpen) { setHelpOpen(false); return; }
      setChromeOpen(false);
    },
    onReopenSignDoc: reopenSignDoc,
  });

  // Help overlay surfaces independently of the chrome panel.
  // Audience-facing chrome: nothing rendered until reveal.
  if (!chromeOpen) {
    return <KeyboardHelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />;
  }

  return (
    <div
      data-chrome-panel="unlocked"
      // Bottom-end corner. Sage-drawer collision handled via the
      // [data-sage-drawer="open"] selector in globals.css (horizontal
      // offset against the right-anchored drawer).
      className="pointer-events-auto fixed bottom-3 end-3 z-50 w-80 max-w-[calc(100vw-1.5rem)] max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-md border border-state-verified/40 bg-orbitra-ink/95 p-3 font-mono text-[10px] shadow-glass transition-[inset-inline-end] duration-state ease-state"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-state-verified uppercase tracking-[0.18em]">Presenter chrome</span>
        <button
          onClick={() => setChromeOpen(false)}
          title="Close panel"
          className="text-orbitra-muted hover:text-orbitra-paper"
        >
          close ✕
        </button>
      </div>

      <ScenarioPlayMenu
        catalogue={catalogue}
        manualMode={manualMode}
        onPlay={(id, manual) => {
          resetUserActions();
          void post(`/control/play?scenario=${id}${manual ? "&manual=true" : ""}`);
          setPaused(false);
        }}
      />

      <ManualModeToggle
        manualMode={manualMode}
        onToggle={() => setManualMode((v) => !v)}
        onNextScreen={() => void post("/control/next")}
      />

      <WarpControls
        warp={warp}
        onWarpChange={setWarp}
        onApply={(w) => void post(`/control/warp?factor=${w}`)}
        manualMode={manualMode}
      />

      <RewindControls
        paused={paused}
        onTogglePause={() => {
          if (paused) { void post("/control/resume"); setPaused(false); }
          else        { void post("/control/pause");  setPaused(true);  }
        }}
        onReset={() => { resetUserActions(); void post("/control/reset"); setPaused(false); }}
        onRewindAct={seekToActBoundary}
        onRewindScreen={seekToPreviousScreen}
      />

      <TimelineScrubber
        currentScreen={ev.currentScreen?.screen}
        screens={(catalogue[inferActiveScenario(ev)]?.screenCheckpoints ?? []) as ScenarioScreenCheckpoint[]}
        onSeek={(offsetMs) => { void post(`/control/seek?offsetMs=${offsetMs}`); setPaused(false); }}
      />

      <InjectMenu onInject={(f) => void post(`/control/inject?fragment=${f}`)} />

      <HotkeysLegend />

      <KeyboardHelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
